import { logDebug, logError, logInfo } from "services/logging";
import assert from "utils/assert";
import { createTallyConversation } from "./endpoints";
import {
  EventOfType,
  parseServerEventData,
  RealtimeClientEvent,
  NamedServerEvent,
  RealtimeServerEvent,
} from "./events";

export type EventHandlerMap = {
  [TType in NamedServerEvent["type"]]?: (event: EventOfType<TType>) => void;
};

export type TallyConnection = Awaited<ReturnType<typeof createTallyConnection>>;

export default async function createTallyConnection(
  eventHandlers: EventHandlerMap,
  onConnectionClosed: () => void
) {
  const { rtcConn, cleanupRtcConn } = await createRtcConnection(() => {
    cleanup();
    onConnectionClosed();
  });
  const inputAudioTrack = await setupInputAudioTrack(rtcConn);
  const outputAudioTrack = setupOutputAudioTrack(rtcConn);
  const dataChannel = createDataChannel(rtcConn, eventHandlers);
  try {
    const conversationId = await openRtcConnection(rtcConn);

    return {
      conversationId,
      close,
      sendRealtimeEvent: dataChannel.sendRealtimeEvent,
      waitForRealtimeEvent: dataChannel.waitForRealtimeEvent,
      toggleInputTrack: inputAudioTrack.toggle,
      toggleOutputTrack: outputAudioTrack.toggle,
    };
  } catch (error) {
    close();
    logError("Error openning WebRTC connection:", error);
    throw error;
  }

  function close() {
    rtcConn.close();
    // A programmatic close() does NOT fire onConnectionStateChange,
    // so we must clean up ourselves.
    cleanup();
  }

  function cleanup() {
    inputAudioTrack.cleanup();
    outputAudioTrack.cleanup();
    dataChannel.cleanup();
    cleanupRtcConn();
  }
}

async function createRtcConnection(onConnectionClosed: () => void) {
  const rtcConn = new RTCPeerConnection();

  const onConnectionStateChange = () => {
    const status = rtcConn.connectionState;
    if (status === "disconnected" || status === "closed" || status === "failed") {
      onConnectionClosed();
    }
  };
  rtcConn.addEventListener("connectionstatechange", onConnectionStateChange);

  return {
    rtcConn,
    cleanupRtcConn: () => {
      rtcConn.removeEventListener("connectionstatechange", onConnectionStateChange);
    },
  };
}

function setupOutputAudioTrack(rtcConnection: RTCPeerConnection) {
  const audioElement = document.createElement("audio");
  let outputAudioStream: MediaStream | undefined;

  rtcConnection.addEventListener("track", onRemoteTrackReceived);
  function onRemoteTrackReceived(event: RTCTrackEvent) {
    logInfo("Received remote track:", event.streams);
    outputAudioStream = event.streams[0];

    audioElement.autoplay = true;
    audioElement.style.display = "none";
    audioElement.srcObject = outputAudioStream;

    // Attach to the DOM: a detached audio element degrades Chrome's echo
    // cancellation because the AEC reference is taken from the render path.
    document.body.appendChild(audioElement);
  }

  return {
    toggle: (enabled: boolean) => {
      assert(outputAudioStream, "No output audio stream to toggle");
      for (const track of outputAudioStream.getTracks()) {
        track.enabled = enabled;
      }
    },
    cleanup: () => {
      rtcConnection.removeEventListener("track", onRemoteTrackReceived);
      outputAudioStream?.getTracks().forEach(track => track.stop());
      audioElement.srcObject = null;
      audioElement.remove();
    },
  };
}

async function setupInputAudioTrack(rtcConnection: RTCPeerConnection) {
  // Get user audio and add to connection. Explicit constraints keep the
  // browser's echo canceller on so Tally's own voice doesn't leak back in.
  const inputAudioStream = await navigator.mediaDevices.getUserMedia({
    audio: {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
    },
  });
  const inputAudioTrack = inputAudioStream.getTracks()[0];
  rtcConnection.addTrack(inputAudioTrack);

  return {
    toggle: (enabled: boolean) => {
      for (const rtpSender of rtcConnection.getSenders()) {
        if (rtpSender.track) {
          rtpSender.track.enabled = enabled;
        }
      }
    },
    cleanup: () => {
      inputAudioStream.getTracks().forEach(track => track.stop());
    },
  };
}

async function openRtcConnection(conn: RTCPeerConnection) {
  const offer = await conn.createOffer();
  await conn.setLocalDescription(offer);
  const sdpResponse = await createTallyConversation(offer);

  await conn.setRemoteDescription({
    type: "answer",
    sdp: await sdpResponse.text(),
  });

  const idHeader = sdpResponse.headers.get("x-tally-conversation-id");
  assert(idHeader, "No call ID returned from Realtime API");

  return idHeader;
}

function createDataChannel(
  rtcConnection: RTCPeerConnection,
  eventHandlers: EventHandlerMap
) {
  const dataChannel = rtcConnection.createDataChannel("oai-events");
  dataChannel.addEventListener("message", onMessage);

  return {
    waitForRealtimeEvent,
    sendRealtimeEvent: (event: RealtimeClientEvent) => {
      if (dataChannel.readyState !== "open") {
        logDebug("Skipping realtime event; data channel not open:", event.type);
        return;
      }
      dataChannel.send(JSON.stringify(event));
    },
    cleanup: () => {
      dataChannel.removeEventListener("message", onMessage);
    },
  };

  function onMessage(domEvent: MessageEvent<string>) {
    const serverEvent = parseServerEventData(domEvent);
    logDebug(`${serverEvent.type}\n\n${JSON.stringify(serverEvent, null, 4)}`);
    // The event's own type picks its handler, so the two always match — but only
    // at runtime, and the socket may send an event this app does not name.
    const handler = eventHandlers[serverEvent.type as NamedServerEvent["type"]] as
      | ((event: RealtimeServerEvent) => void)
      | undefined;
    handler?.(serverEvent);
  }

  // Listen for a single server event matching a type an optional filter
  function waitForRealtimeEvent<T extends RealtimeServerEvent["type"]>(
    type: T,
    eventCondition: (event: EventOfType<T>) => boolean = () => true,
    timeout = 10000
  ): Promise<EventOfType<T>> {
    return new Promise((resolve, reject) => {
      const eventHandler = (domEvent: MessageEvent<string>) => {
        const serverEvent = parseServerEventData(domEvent);
        if (serverEvent.type === type && eventCondition(serverEvent as EventOfType<T>)) {
          resolve(serverEvent as EventOfType<T>);
          dataChannel.removeEventListener("message", eventHandler);
          clearTimeout(timeoutId);
        }
      };

      dataChannel.addEventListener("message", eventHandler);

      const timeoutId = setTimeout(() => {
        dataChannel.removeEventListener("message", eventHandler);
        // A timeout isn't inherently an error so no logging.
        reject(new Error(`Timeout waiting for event ${type}`));
      }, timeout);
    });
  }
}
